import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { EmptySiteNotice } from '../common/EmptySiteNotice';
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  Clock,
  Globe,
  Share2,
  Save,
  CheckCircle2,
  Sparkles,
  ShieldCheck,
  Plus,
  X,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Twitter,
  Calendar,
  FileText,
  Map,
} from 'lucide-react';

export const BusinessProfileManager: React.FC<{ onNavigate?: (view: string) => void }> = ({ onNavigate }) => {
  const { activeSite, updateSiteData } = useSite();

  const s = activeSite?.settings || {};

  const [businessName, setBusinessName] = useState(s.businessName || activeSite?.name || '');
  const [shortDescription, setShortDescription] = useState(
    s.shortDescription || s.tagline || 'Profesyonel kurumsal çözümler ve müşteri odaklı hizmetler.'
  );
  const [longDescription, setLongDescription] = useState(
    s.longDescription ||
      'Sektördeki uzun yıllara dayanan deneyimimiz, uzman kadromuz ve son teknoloji donanımlarımız ile müşterilerimize en üstün standartlarda güvenilir hizmet sağlıyoruz.'
  );
  const [phone, setPhone] = useState(s.phone || '+90 (252) 692 00 00');
  const [whatsapp, setWhatsapp] = useState(s.whatsapp || s.phone || '+90 (544) 000 00 00');
  const [email, setEmail] = useState(s.email || 'info@siteniz.com');
  const [address, setAddress] = useState(s.address || 'Atatürk Caddesi No: 45 / Dalaman');
  const [city, setCity] = useState(s.city || 'Muğla');
  const [district, setDistrict] = useState(s.district || 'Dalaman');
  const [workingHours, setWorkingHours] = useState(s.workingHours || 'Pazartesi - Cumartesi: 08:30 - 19:30');
  const [googleMapsUrl, setGoogleMapsUrl] = useState(s.googleMapsUrl || '');
  const [googleBusinessProfileUrl, setGoogleBusinessProfileUrl] = useState(s.googleBusinessProfileUrl || '');
  const [taxOffice, setTaxOffice] = useState(s.taxOffice || 'Dalaman Vergi Dairesi');
  const [taxNumber, setTaxNumber] = useState(s.taxNumber || '');
  const [foundedYear, setFoundedYear] = useState(s.foundedYear ? String(s.foundedYear) : '2016');
  const [logoUrl, setLogoUrl] = useState(s.logoUrl || '');
  const [faviconUrl, setFaviconUrl] = useState(s.faviconUrl || '');

  // Social Links
  const [instagram, setInstagram] = useState(s.socialLinks?.instagram || '');
  const [facebook, setFacebook] = useState(s.socialLinks?.facebook || '');
  const [linkedin, setLinkedin] = useState(s.socialLinks?.linkedin || '');
  const [youtube, setYoutube] = useState(s.socialLinks?.youtube || '');

  // Service Areas Tags
  const [serviceAreas, setServiceAreas] = useState<string[]>(
    s.serviceAreas || ['Dalaman', 'Ortaca', 'Köyceğiz', 'Göcek', 'Fethiye']
  );
  const [newAreaInput, setNewAreaInput] = useState('');

  const [savedSuccess, setSavedSuccess] = useState(false);

  useEffect(() => {
    if (activeSite) {
      const setts = activeSite.settings || {};
      setBusinessName(setts.businessName || activeSite.name || '');
      setShortDescription(setts.shortDescription || setts.tagline || '');
      setLongDescription(setts.longDescription || '');
      setPhone(setts.phone || '');
      setWhatsapp(setts.whatsapp || setts.phone || '');
      setEmail(setts.email || '');
      setAddress(setts.address || '');
      setCity(setts.city || '');
      setDistrict(setts.district || '');
      setWorkingHours(setts.workingHours || '');
      setGoogleMapsUrl(setts.googleMapsUrl || '');
      setGoogleBusinessProfileUrl(setts.googleBusinessProfileUrl || '');
      setTaxOffice(setts.taxOffice || '');
      setTaxNumber(setts.taxNumber || '');
      setFoundedYear(setts.foundedYear ? String(setts.foundedYear) : '');
      setLogoUrl(setts.logoUrl || '');
      setFaviconUrl(setts.faviconUrl || '');
      setInstagram(setts.socialLinks?.instagram || '');
      setFacebook(setts.socialLinks?.facebook || '');
      setLinkedin(setts.socialLinks?.linkedin || '');
      setYoutube(setts.socialLinks?.youtube || '');
      setServiceAreas(setts.serviceAreas || []);
    }
  }, [activeSite?.id]);

  if (!activeSite) {
    return (
      <EmptySiteNotice
        title="İşletme Profili İçin Aktif Web Sitesi Gereklidir"
        description="Firma bilgileri, iletişim kanalları, çalışma saatleri ve sosyal medya bağlantılarını yönetebilmek için lütfen bir web sitesi oluşturun veya mevcut sitelerinizden birini aktif yapın."
        onOpenCreateSite={() => onNavigate && onNavigate('sites')}
        onReturnToDashboard={() => onNavigate && onNavigate('dashboard')}
      />
    );
  }

  const handleAddArea = () => {
    if (!newAreaInput.trim()) return;
    if (!serviceAreas.includes(newAreaInput.trim())) {
      setServiceAreas([...serviceAreas, newAreaInput.trim()]);
    }
    setNewAreaInput('');
  };

  const handleRemoveArea = (area: string) => {
    setServiceAreas(serviceAreas.filter((a) => a !== area));
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateSiteData({
      name: businessName || activeSite?.name || '',
      settings: {
        ...(activeSite?.settings || {}),
        businessName,
        siteName: businessName,
        shortDescription,
        longDescription,
        tagline: shortDescription,
        phone,
        whatsapp,
        email,
        address,
        city,
        district,
        workingHours,
        googleMapsUrl,
        googleBusinessProfileUrl,
        taxOffice,
        taxNumber,
        foundedYear,
        logoUrl,
        faviconUrl,
        serviceAreas,
        socialLinks: {
          instagram,
          facebook,
          linkedin,
          youtube,
          whatsapp,
        },
      },
    });

    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 3000);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6 animate-in fade-in select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2 text-blue-600">
            <Building2 className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">Merkezi Veritabanı</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
            Firma ve İşletme Bilgileri
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 max-w-2xl">
            Burada gireceğiniz işletme bilgileri web sitenizin Header, Hero, İletişim, WhatsApp, Harita ve Footer bileşenlerine otomatik olarak bağlanır.
          </p>
        </div>

        <button
          type="button"
          onClick={handleSave}
          className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-lg shadow-blue-500/20 transition flex items-center gap-2 self-start sm:self-auto"
        >
          {savedSuccess ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : <Save className="w-4 h-4" />}
          <span>{savedSuccess ? 'Kaydedildi ✓' : 'Değişiklikleri Kaydet'}</span>
        </button>
      </div>

      {savedSuccess && (
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs sm:text-sm font-medium flex items-center gap-3 animate-in fade-in">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <span>Firma bilgileri başarıyla güncellendi. Web sitenizdeki tüm ilgili bileşenler otomatik olarak yenilendi.</span>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* 1. Temel Şirket Kimliği */}
        <div className="p-6 rounded-2xl bg-white border border-slate-200/80 shadow-xs space-y-5">
          <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
            <Building2 className="w-5 h-5 text-blue-600" />
            <h2 className="text-base font-bold text-slate-900">1. Temel İşletme Kimliği</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-xs font-bold text-slate-700">Resmi Firma / İşletme Adı *</label>
              <input
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Örn: Dalaman Halı Yıkama & Temizlik Hizmetleri"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-sm font-semibold focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-hidden transition"
                required
              />
              <p className="text-[11px] text-slate-400">Web sitesinin başlığında, kurumsal menüde ve telif alanında gösterilir.</p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Kısa Slogan / Özet Tanım</label>
              <input
                type="text"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                placeholder="Örn: Dalaman ve Çevresinde Profesyonel Halı, Koltuk ve Perde Yıkama"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs sm:text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-hidden transition"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Kuruluş Yılı</label>
              <input
                type="text"
                value={foundedYear}
                onChange={(e) => setFoundedYear(e.target.value)}
                placeholder="Örn: 2016"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs sm:text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-hidden transition"
              />
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <label className="text-xs font-bold text-slate-700">Detaylı Kurumsal Tanıtım (Hakkımızda Metni Kaynağı)</label>
              <textarea
                rows={3}
                value={longDescription}
                onChange={(e) => setLongDescription(e.target.value)}
                placeholder="İşletmenizin tarihçesi, uzmanlıkları ve hizmet anlayışını anlatan kurumsal metin..."
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs sm:text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-hidden transition leading-relaxed"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Logo Görsel URL</label>
              <input
                type="text"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://... veya Medya Kütüphanesinden seçin"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-hidden transition font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Favicon URL (Tarayıcı Sekme İkonu)</label>
              <input
                type="text"
                value={faviconUrl}
                onChange={(e) => setFaviconUrl(e.target.value)}
                placeholder="https://.../favicon.ico"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-hidden transition font-mono"
              />
            </div>
          </div>
        </div>

        {/* 2. İletişim ve Hızlı Aksiyon Bilgileri */}
        <div className="p-6 rounded-2xl bg-white border border-slate-200/80 shadow-xs space-y-5">
          <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
            <Phone className="w-5 h-5 text-emerald-600" />
            <h2 className="text-base font-bold text-slate-900">2. İletişim, WhatsApp ve Çalışma Saatleri</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Telefon Numarası (Arama İçin) *</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0544 XXX XX XX veya 0252 XXX XX XX"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-sm font-semibold focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-hidden transition"
                required
              />
              <p className="text-[10px] text-slate-400">Mobilde tek tıkla arama butonu bu numarayı bağlar.</p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">WhatsApp Destek Hattı *</label>
              <input
                type="text"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="+90 (544) 000 00 00"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-sm font-semibold focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-hidden transition"
                required
              />
              <p className="text-[10px] text-slate-400">Sabit WhatsApp sohbet butonu bu numarayı açar.</p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">E-Posta Adresi</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="info@sirketiniz.com"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-hidden transition"
              />
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <label className="text-xs font-bold text-slate-700">Açık Adres</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Cadde, Sokak, Kapı No, Posta Kodu"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs sm:text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-hidden transition"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Çalışma Saatleri</label>
              <input
                type="text"
                value={workingHours}
                onChange={(e) => setWorkingHours(e.target.value)}
                placeholder="Pzt - Cmt: 08:30 - 19:30"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs sm:text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-hidden transition"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">İl (Şehir)</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Muğla"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs sm:text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-hidden transition"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">İlçe</label>
              <input
                type="text"
                value={district}
                onChange={(e) => setDistrict(e.target.value)}
                placeholder="Dalaman"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs sm:text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-hidden transition"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Google Haritalar Embed / Konum URL</label>
              <input
                type="text"
                value={googleMapsUrl}
                onChange={(e) => setGoogleMapsUrl(e.target.value)}
                placeholder="https://maps.google.com/..."
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-hidden transition font-mono"
              />
            </div>
          </div>
        </div>

        {/* 3. Yerel Hizmet Bölgeleri */}
        <div className="p-6 rounded-2xl bg-white border border-slate-200/80 shadow-xs space-y-5">
          <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
            <Map className="w-5 h-5 text-indigo-600" />
            <h2 className="text-base font-bold text-slate-900">3. Hizmet Verilen Bölgeler (Local SEO)</h2>
          </div>

          <div className="space-y-3">
            <p className="text-xs text-slate-500 leading-relaxed">
              İşletmenizin servis sağladığı ilçe, mahalle ve çevre lokasyonlar web sitenizin yerel arama motoru optimizasyonu için etiketlenir.
            </p>

            <div className="flex flex-wrap gap-2 pt-1">
              {serviceAreas.map((area) => (
                <span
                  key={area}
                  className="px-3 py-1.5 rounded-xl bg-blue-50 text-blue-700 border border-blue-200 text-xs font-semibold flex items-center gap-1.5 shadow-2xs"
                >
                  <span>{area}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveArea(area)}
                    className="p-0.5 rounded-full hover:bg-blue-200 text-blue-500 transition"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>

            <div className="flex items-center gap-2 pt-2 max-w-md">
              <input
                type="text"
                value={newAreaInput}
                onChange={(e) => setNewAreaInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddArea();
                  }
                }}
                placeholder="Yeni bölge ekle (örn: Göcek, Dalyan)"
                className="flex-1 px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs text-slate-900 focus:bg-white focus:border-blue-500 outline-hidden transition"
              />
              <button
                type="button"
                onClick={handleAddArea}
                className="px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Ekle</span>
              </button>
            </div>
          </div>
        </div>

        {/* 4. Sosyal Medya Hesapları */}
        <div className="p-6 rounded-2xl bg-white border border-slate-200/80 shadow-xs space-y-5">
          <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
            <Share2 className="w-5 h-5 text-purple-600" />
            <h2 className="text-base font-bold text-slate-900">4. Sosyal Medya Hesapları</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <Instagram className="w-4 h-4 text-pink-500" />
                <span>Instagram URL</span>
              </label>
              <input
                type="text"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                placeholder="https://instagram.com/firma_hesabi"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs focus:bg-white focus:border-blue-500 outline-hidden transition font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <Facebook className="w-4 h-4 text-blue-600" />
                <span>Facebook URL</span>
              </label>
              <input
                type="text"
                value={facebook}
                onChange={(e) => setFacebook(e.target.value)}
                placeholder="https://facebook.com/firma_sayfasi"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs focus:bg-white focus:border-blue-500 outline-hidden transition font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <Linkedin className="w-4 h-4 text-blue-700" />
                <span>LinkedIn URL</span>
              </label>
              <input
                type="text"
                value={linkedin}
                onChange={(e) => setLinkedin(e.target.value)}
                placeholder="https://linkedin.com/company/..."
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs focus:bg-white focus:border-blue-500 outline-hidden transition font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <Youtube className="w-4 h-4 text-red-600" />
                <span>YouTube Kanalı</span>
              </label>
              <input
                type="text"
                value={youtube}
                onChange={(e) => setYoutube(e.target.value)}
                placeholder="https://youtube.com/@kanal"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs focus:bg-white focus:border-blue-500 outline-hidden transition font-mono"
              />
            </div>
          </div>
        </div>

        {/* 5. Resmi Vergi ve Şirket Bilgileri */}
        <div className="p-6 rounded-2xl bg-white border border-slate-200/80 shadow-xs space-y-5">
          <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
            <ShieldCheck className="w-5 h-5 text-slate-700" />
            <h2 className="text-base font-bold text-slate-900">5. Yasal & Vergi Bilgileri (Güven Unsuru)</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Bağlı Olduğu Vergi Dairesi</label>
              <input
                type="text"
                value={taxOffice}
                onChange={(e) => setTaxOffice(e.target.value)}
                placeholder="Örn: Dalaman Vergi Dairesi"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs sm:text-sm focus:bg-white focus:border-blue-500 outline-hidden transition"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Vergi / MERSİS Numarası</label>
              <input
                type="text"
                value={taxNumber}
                onChange={(e) => setTaxNumber(e.target.value)}
                placeholder="10 veya 11 haneli vergi no"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 text-xs sm:text-sm focus:bg-white focus:border-blue-500 outline-hidden transition"
              />
            </div>
          </div>
        </div>

        {/* Bottom Save Action */}
        <div className="flex items-center justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={handleSave}
            className="w-full sm:w-auto px-8 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm shadow-xl shadow-blue-500/25 transition flex items-center justify-center gap-2"
          >
            {savedSuccess ? <CheckCircle2 className="w-5 h-5 text-emerald-300" /> : <Save className="w-5 h-5" />}
            <span>{savedSuccess ? 'Tüm Bilgiler Kaydedildi ✓' : 'Firma Bilgilerini Kaydet'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};
